import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import Select from 'react-select';
import { SingleDatePicker } from 'react-dates';
import 'react-dates/lib/css/_datepicker.css';
import styledComponent from '../../utils/styledComponent';
import PalletSlip from '../PackingSlips/PalletSlip';
import PalletSummaryCard from './PalletSummaryCard';
import StatusBadge from './StatusBadge';
import useFetchPallets from './useFetchPallets';

const SectionHeader = styledComponent('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '20px',
  '& h4': {
    margin: 0,
    fontWeight: 500,
  },
  '& p': {
    margin: '5px 0 0 0',
    color: '#818387',
    fontSize: '14px',
  },
});

const FilterRow = styledComponent('div', {
  marginBottom: '20px',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '20px',
});

const FilterGroup = styledComponent('div', {
  display: 'inline-block',
  minWidth: '200px',
  '& label': {
    color: '#000',
    fontSize: '18px',
    fontWeight: 800,
    marginBottom: '10px',
    display: 'block',
  },
  '& .SingleDatePickerInput__withBorder': {
    border: 'none',
  },
  '& .SingleDatePicker': {
    display: 'block',
  },
  '& .SingleDatePicker input': {
    background: '#FFF',
    border: '1px solid #CCC',
    borderRadius: '4px',
    fontSize: '16px',
    height: '50px',
    padding: '15px',
    width: '200px',
  },
});

const StatusSummary = styledComponent('div', {
  marginBottom: '20px',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '15px',
});

const WarningBanner = styledComponent('div', {
  background: '#fff3cd',
  border: '1px solid #ff9e22',
  color: '#856404',
  padding: '12px 16px',
  borderRadius: '4px',
  marginBottom: '20px',
  fontSize: '14px',
});

const LoadingMessage = styledComponent('div', {
  textAlign: 'center',
  padding: '40px',
  color: '#818387',
  fontSize: '16px',
});

const NoDataMessage = styledComponent('div', {
  textAlign: 'center',
  padding: '40px',
  color: '#818387',
  fontSize: '16px',
});

const selectStyles = {
  control: (baseStyles) => ({
    ...baseStyles,
    borderRadius: '4px',
    border: '1px solid #CCC',
    minHeight: '50px',
    minWidth: '200px',
  }),
};

const STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'LOADED', label: 'Loaded' },
  { value: 'RECEIVED', label: 'Received' },
  { value: 'VARIANCE', label: 'Variance' },
];

const GeneratedPalletsTab = ({ selectedDate, onDateChange, isActive }) => {
  const [isDateFocused, setIsDateFocused] = useState(false);
  const { pallets, isLoading, error, fetchPallets } =
    useFetchPallets(selectedDate);

  useEffect(() => {
    if (isActive) {
      fetchPallets();
    }
  }, [isActive, fetchPallets]);

  // Filter states
  const [linehaulFilter, setLinehaulFilter] = useState(null);
  const [middleMileFilter, setMiddleMileFilter] = useState(null);
  const [regionFilter, setRegionFilter] = useState(null);
  const [fulfillmentCenterFilter, setFulfillmentCenterFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);

  // Extract unique filter options from pallets
  const filterOptions = useMemo(() => {
    const linehauls = new Set();
    const middleMiles = new Set();
    const regions = new Set();
    const fulfillmentCenters = new Set();

    pallets.forEach((pallet) => {
      if (pallet.linehaulFirstMile) linehauls.add(pallet.linehaulFirstMile);
      if (pallet.linehaulMiddleMile) middleMiles.add(pallet.linehaulMiddleMile);
      if (pallet.region) regions.add(pallet.region);
      if (pallet.fulfillmentCenter)
        fulfillmentCenters.add(pallet.fulfillmentCenter);
    });

    return {
      linehauls: Array.from(linehauls).map((v) => ({ value: v, label: v })),
      middleMiles: Array.from(middleMiles).map((v) => ({ value: v, label: v })),
      regions: Array.from(regions).map((v) => ({ value: v, label: v })),
      fulfillmentCenters: Array.from(fulfillmentCenters).map((v) => ({
        value: v,
        label: v,
      })),
    };
  }, [pallets]);

  // Filter pallets based on selected filters
  const filteredPallets = useMemo(() => {
    return pallets.filter((pallet) => {
      if (linehaulFilter && pallet.linehaulFirstMile !== linehaulFilter.value)
        return false;
      if (
        middleMileFilter &&
        pallet.linehaulMiddleMile !== middleMileFilter.value
      )
        return false;
      if (regionFilter && pallet.region !== regionFilter.value) return false;
      if (
        fulfillmentCenterFilter &&
        pallet.fulfillmentCenter !== fulfillmentCenterFilter.value
      )
        return false;
      if (statusFilter && pallet.loadStatus !== statusFilter.value)
        return false;
      return true;
    });
  }, [
    pallets,
    linehaulFilter,
    middleMileFilter,
    regionFilter,
    fulfillmentCenterFilter,
    statusFilter,
  ]);

  // Calculate status counts
  const statusCounts = useMemo(() => {
    const counts = {
      PENDING: 0,
      LOADED: 0,
      RECEIVED: 0,
      VARIANCE: 0,
    };

    filteredPallets.forEach((pallet) => {
      const status = pallet.loadStatus || 'PENDING';
      if (counts[status] !== undefined) {
        counts[status] += 1;
      }
    });

    return counts;
  }, [filteredPallets]);

  // Group pallets by linehaul
  const palletsByLinehaul = useMemo(() => {
    const grouped = {};
    filteredPallets.forEach((pallet) => {
      const linehaul = pallet.linehaulFirstMile || 'Unknown';
      if (!grouped[linehaul]) {
        grouped[linehaul] = [];
      }
      grouped[linehaul].push(pallet);
    });
    return grouped;
  }, [filteredPallets]);

  // Calculate summary data for each linehaul
  const getLinehaulSummary = (linehaulPallets) => {
    const region = linehaulPallets[0]?.region || 'Unknown';
    const middleMile = linehaulPallets[0]?.linehaulMiddleMile || null;
    const totalPallets = linehaulPallets.length;
    const marketBreakdown = {};

    linehaulPallets.forEach((pallet) => {
      const market = pallet.market || 'Unknown';
      marketBreakdown[market] = (marketBreakdown[market] || 0) + 1;
    });

    return { region, middleMile, totalPallets, marketBreakdown };
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div style={{ paddingTop: '20px' }}>
      <SectionHeader className="no-print">
        <div>
          <h4>Generated Pallets</h4>
          <p>Post-allocation actual pallets with status tracking</p>
        </div>
        <Button onClick={handlePrint}>
          <span className="glyphicon glyphicon-print" /> Print Pallet Slips
        </Button>
      </SectionHeader>

      <FilterRow className="no-print">
        <FilterGroup>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="gen-production-date">Production Date</label>
          <SingleDatePicker
            date={selectedDate}
            isOutsideRange={() => false}
            onDateChange={onDateChange}
            focused={isDateFocused}
            onFocusChange={({ focused }) => setIsDateFocused(focused)}
            id="gen-production-date"
            numberOfMonths={1}
          />
        </FilterGroup>

        <FilterGroup>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="gen-linehaul-filter">Linehaul</label>
          <Select
            inputId="gen-linehaul-filter"
            options={filterOptions.linehauls}
            value={linehaulFilter}
            onChange={setLinehaulFilter}
            isClearable
            placeholder="All"
            styles={selectStyles}
          />
        </FilterGroup>

        <FilterGroup>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="gen-middle-mile-filter">Middle Mile</label>
          <Select
            inputId="gen-middle-mile-filter"
            options={filterOptions.middleMiles}
            value={middleMileFilter}
            onChange={setMiddleMileFilter}
            isClearable
            placeholder="All"
            styles={selectStyles}
          />
        </FilterGroup>

        <FilterGroup>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="gen-region-filter">Region</label>
          <Select
            inputId="gen-region-filter"
            options={filterOptions.regions}
            value={regionFilter}
            onChange={setRegionFilter}
            isClearable
            placeholder="All"
            styles={selectStyles}
          />
        </FilterGroup>

        <FilterGroup>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="gen-fc-filter">Fulfillment Center</label>
          <Select
            inputId="gen-fc-filter"
            options={filterOptions.fulfillmentCenters}
            value={fulfillmentCenterFilter}
            onChange={setFulfillmentCenterFilter}
            isClearable
            placeholder="All"
            styles={selectStyles}
          />
        </FilterGroup>

        <FilterGroup>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="gen-status-filter">Status</label>
          <Select
            inputId="gen-status-filter"
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
            isClearable
            placeholder="All"
            styles={selectStyles}
          />
        </FilterGroup>
      </FilterRow>

      {!isLoading && !error && filteredPallets.length > 0 && (
        <StatusSummary className="no-print">
          <strong style={{ marginRight: '20px' }}>
            Summary: {filteredPallets.length} total
          </strong>
          {statusCounts.PENDING > 0 && (
            <StatusBadge status="PENDING" count={statusCounts.PENDING} />
          )}
          {statusCounts.LOADED > 0 && (
            <StatusBadge status="LOADED" count={statusCounts.LOADED} />
          )}
          {statusCounts.RECEIVED > 0 && (
            <StatusBadge status="RECEIVED" count={statusCounts.RECEIVED} />
          )}
          {statusCounts.VARIANCE > 0 && (
            <StatusBadge status="VARIANCE" count={statusCounts.VARIANCE} />
          )}
        </StatusSummary>
      )}

      {isLoading && <LoadingMessage>Loading pallet data...</LoadingMessage>}

      {error && (
        <WarningBanner>
          <strong>Error:</strong> {error}
        </WarningBanner>
      )}

      {!isLoading && !error && filteredPallets.length === 0 && (
        <NoDataMessage>
          No generated pallets available for the selected date and filters.
        </NoDataMessage>
      )}

      {!isLoading && !error && filteredPallets.length > 0 && (
        <>
          {Object.entries(palletsByLinehaul).map(
            ([linehaul, linehaulPallets]) => {
              const { region, middleMile, totalPallets, marketBreakdown } =
                getLinehaulSummary(linehaulPallets);

              return (
                <div key={linehaul}>
                  <PalletSummaryCard
                    linehaul={linehaul}
                    region={region}
                    middleMile={middleMile}
                    totalPallets={totalPallets}
                    marketBreakdown={marketBreakdown}
                  />

                  {linehaulPallets.map((pallet, index) => (
                    <PalletSlip
                      key={pallet.uniqueId || index}
                      pallet={pallet}
                      palletIndex={index}
                      totalPallets={totalPallets}
                    />
                  ))}
                </div>
              );
            },
          )}

          <p className="text-muted no-print" style={{ textAlign: 'center' }}>
            Showing {filteredPallets.length} pallet
            {filteredPallets.length !== 1 ? 's' : ''}.
          </p>
        </>
      )}
    </div>
  );
};

GeneratedPalletsTab.propTypes = {
  selectedDate: PropTypes.object,
  onDateChange: PropTypes.func.isRequired,
  isActive: PropTypes.bool,
};

GeneratedPalletsTab.defaultProps = {
  selectedDate: null,
  isActive: false,
};

export default GeneratedPalletsTab;
